import { useTheme } from '../lib/theme-provider';
import { ThemedCard } from './ThemedCard';
import { ThemedButton } from './ThemedButton';
import { History, X, Users, Check, X as XIcon, Crown } from 'lucide-react';

interface Vote {
  playerId: string;
  nickname: string;
  seatNumber: number;
  vote: 'approve' | 'reject';
}

interface Mission {
  round: number;
  leader: string;
  leaderSeat: number;
  team: number[];
  votes: Vote[];
  result: 'success' | 'fail';
  failCount: number;
}

interface GameHistoryModalProps {
  isOpen: boolean;
  missions: Mission[];
  onClose: () => void;
}

export default function GameHistoryModal({
  isOpen,
  missions,
  onClose,
}: GameHistoryModalProps) {
  const { theme } = useTheme();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
    >
      <ThemedCard className="w-full max-w-md max-h-[80vh] p-6 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div
            className="flex items-center gap-2"
            style={{ color: theme.colors['accent-gold'] }}
          >
            <History size={24} />
            <h2
              className="text-xl font-bold"
              style={{ color: theme.colors['text-primary'] }}
            >
              游戏历史
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ backgroundColor: theme.colors['bg-primary'] }}
          >
            <X size={20} style={{ color: theme.colors['text-secondary'] }} />
          </button>
        </div>

        {/* 历史列表 */}
        <div
          className="overflow-y-auto max-h-[60vh] space-y-3"
          style={{ scrollbarWidth: 'thin' }}
        >
          {missions.length === 0 ? (
            <div
              className="text-center py-8 text-sm"
              style={{ color: theme.colors['text-muted'] }}
            >
              暂无历史记录
            </div>
          ) : (
            missions.map((mission) => (
              <div
                key={mission.round}
                className="p-4 rounded-xl"
                style={{
                  backgroundColor: theme.colors['bg-primary'],
                  border: `1px solid ${theme.colors['border-secondary']}`,
                }}
              >
                {/* 轮次标题 */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-sm font-bold"
                      style={{ color: theme.colors['text-primary'] }}
                    >
                      第{mission.round}轮
                    </span>
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs"
                      style={{
                        backgroundColor:
                          mission.result === 'success'
                            ? '#4ade80'
                            : '#f87171',
                        color: theme.colors['bg-primary'],
                      }}
                    >
                      {mission.result === 'success' ? '✓' : '✗'}
                    </div>
                  </div>
                  <div
                    className="flex items-center gap-1 text-xs"
                    style={{ color: theme.colors['text-muted'] }}
                  >
                    <Crown size={12} />
                    <span>队长: {mission.leader}</span>
                  </div>
                </div>

                {/* 任务队员 */}
                <div className="flex items-center gap-2 mb-3">
                  <Users size={14} style={{ color: theme.colors['text-muted'] }} />
                  <span
                    className="text-xs"
                    style={{ color: theme.colors['text-secondary'] }}
                  >
                    队员:
                  </span>
                  <div className="flex gap-1">
                    {mission.team.map((seat) => (
                      <span
                        key={seat}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: `${theme.colors['accent-gold']}20`,
                          color: theme.colors['accent-gold'],
                        }}
                      >
                        #{seat}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 投票结果 */}
                <div className="mb-3">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Check size={14} style={{ color: '#4ade80' }} />
                      <span
                        className="text-xs"
                        style={{ color: '#4ade80' }}
                      >
                        {mission.votes.filter((v) => v.vote === 'approve').length}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <XIcon size={14} style={{ color: '#f87171' }} />
                      <span
                        className="text-xs"
                        style={{ color: '#f87171' }}
                      >
                        {mission.votes.filter((v) => v.vote === 'reject').length}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 投票详情 */}
                <div
                  className="pt-3 border-t"
                  style={{ borderColor: theme.colors['border-secondary'] }}
                >
                  <div className="grid grid-cols-5 gap-1">
                    {mission.votes.map((vote) => (
                      <div
                        key={vote.playerId}
                        className="flex flex-col items-center"
                      >
                        <span
                          className="text-xs truncate w-full text-center"
                          style={{ color: theme.colors['text-muted'] }}
                        >
                          {vote.nickname}
                        </span>
                        <span
                          className="text-xs"
                          style={{
                            color:
                              vote.vote === 'approve'
                                ? '#4ade80'
                                : '#f87171',
                          }}
                        >
                          {vote.vote === 'approve' ? '✓' : '✗'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 任务结果详情 */}
                {mission.result && (
                  <div
                    className="mt-3 pt-3 border-t flex items-center justify-between"
                    style={{ borderColor: theme.colors['border-secondary'] }}
                  >
                    <span
                      className="text-xs"
                      style={{ color: theme.colors['text-muted'] }}
                    >
                      任务结果
                    </span>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs"
                        style={{
                          color:
                            mission.result === 'success'
                              ? '#4ade80'
                              : '#f87171',
                        }}
                      >
                        {mission.result === 'success' ? '成功' : '失败'}
                      </span>
                      {mission.failCount > 0 && (
                        <span
                          className="px-2 py-0.5 rounded text-xs"
                          style={{
                            backgroundColor: '#f87171',
                            color: theme.colors['bg-primary'],
                          }}
                        >
                          {mission.failCount}失败
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* 关闭按钮 */}
        <ThemedButton onClick={onClose} className="w-full mt-4">
          <X size={18} className="mr-1" />
          关闭
        </ThemedButton>
      </ThemedCard>
    </div>
  );
}
